import { useTranslation } from "react-i18next";
import { ArrowIcon, EmailIcon, PhoneIcon } from "@/assets/icons";
import mmLtLogo from "@/assets/images/MM-LT-logo.svg";
import mmEnLogo from "@/assets/images/MM-ENG-logo.svg";
import euFundingLogo from "@/assets/images/LT_Finansuoja_Europos_Sąjunga_RGB_NEG.png";
import nklLogo from "@/assets/images/NKL-Logo_Horizontal-RGB-White.svg";
import { getRuntimeConfig } from "@/loadConfig";

interface ContactSectionProps {
  title: string;
  phone: string;
  subtext?: string;
  phoneAbroad?: string;
}

function ContactSection({
  title,
  phone,
  subtext,
  phoneAbroad,
}: ContactSectionProps) {
  const formatPhoneForTel = (phoneNumber: string) => {
    return phoneNumber.replace(/\s/g, "");
  };

  return (
    <div className="space-y-2">
      <h3 className="font-bold text-sm text-secondary-400">{title}</h3>
      <a
        href={`tel:${formatPhoneForTel(phone)}`}
        className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
      >
        <PhoneIcon size={18} className="shrink-0" />
        <span>{phone}</span>
      </a>
      {subtext && phoneAbroad && (
        <>
          <p className="text-sm">{subtext}</p>
          <a
            href={`tel:${formatPhoneForTel(phoneAbroad)}`}
            className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
          >
            <PhoneIcon size={18} className="shrink-0" />
            <span>{phoneAbroad}</span>
          </a>
        </>
      )}
    </div>
  );
}

function Footer() {
  const { t, i18n } = useTranslation();

  const config = getRuntimeConfig<Record<string, string>>();
  const mmUrl = config?.["mm-url"];

  const logo = i18n.language.toUpperCase() === "EN" ? mmEnLogo : mmLtLogo;

  return (
    <footer className="bg-layout-palette text-white py-12 mt-auto">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr] gap-12 mb-8">
          <div className="space-y-8">
            <div className="flex items-start gap-6">
              <img
                className="h-16 w-auto shrink-0 my-auto"
                src={logo}
                alt="LR muitinė"
              />
            </div>
            <a
              href={mmUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-gray-400 px-12 py-2 text-sm hover:bg-white hover:text-layout-palette transition-colors rounded inline-flex items-center gap-2 w-fit"
            >
              <span>{t("footer.lrCustomsWebsite")}</span>
              <ArrowIcon direction="up-right" size={16} className="shrink-0" />
            </a>
            <div className="space-y-2 text-sm">
              <p className="font-bold text-secondary-400">
                {t("footer.questionsLabel")}
              </p>
              <a
                href={`mailto:${t("footer.questionsEmail")}`}
                className="flex items-center gap-2 transition-colors hover:opacity-80"
              >
                <EmailIcon size={18} className="shrink-0" />
                <span>{t("footer.questionsEmail")}</span>
              </a>
            </div>
            <div className="space-y-1 text-sm">
              <p className="font-bold text-secondary-400">
                {t("footer.workingHours")}
              </p>
              <p>{t("footer.workingHoursTime")}</p>
            </div>
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <ContactSection
              title={t("footer.generalQuestionsTitle")}
              phone={t("footer.generalQuestionsPhone")}
              subtext={t("footer.callFromAbroad")}
              phoneAbroad={t("footer.generalQuestionsPhoneAbroad")}
            />

            <div className="space-y-2">
              <h3 className="font-bold text-sm text-secondary-400">
                {t("footer.itCenterTitle")}
              </h3>
              <a
                href={`tel:${t("footer.itCenterPhone").replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
              >
                <PhoneIcon size={18} className="shrink-0" />
                <span>{t("footer.itCenterPhone")}</span>
              </a>
              <a
                href={`mailto:${t("footer.itCenterEmail")}`}
                className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
              >
                <EmailIcon size={18} className="shrink-0" />
                <span>{t("footer.itCenterEmail")}</span>
              </a>
            </div>

            <ContactSection
              title={t("footer.declarationsTitle")}
              phone={t("footer.declarationsPhone")}
              subtext={t("footer.callFromAbroad")}
              phoneAbroad={t("footer.declarationsPhoneAbroad")}
            />

            <div className="space-y-2">
              <h3 className="font-bold text-sm text-secondary-400">
                {t("footer.supportHotlineTitle")}
              </h3>
              <a
                href={`tel:${t("footer.supportHotline").replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
              >
                <PhoneIcon size={18} className="shrink-0" />
                <span>{t("footer.supportHotline")}</span>
              </a>
              <p className="text-sm">{t("footer.callFromAbroad")}</p>
              <a
                href={`tel:${t("footer.supportHotlineAbroad").replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm transition-colors hover:opacity-80"
              >
                <PhoneIcon size={18} className="shrink-0" />
                <span>{t("footer.supportHotlineAbroad")}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="pt-6 grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr] gap-12 items-center mt-8">
          <p className="text-sm text-left">{t("footer.dataStorage")}</p>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
            <div className="flex justify-start">
              <img
                src={euFundingLogo}
                alt="Finansuoja Europos Sąjunga"
                className="h-14 w-auto"
              />
            </div>
            <div className="flex justify-start">
              <img src={nklLogo} alt="NKL Logo" className="h-14 w-auto" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
